import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, MenuItem, Alert, Snackbar, Autocomplete, CircularProgress, Paper,
} from '@mui/material';
import { TaskAlt } from '@mui/icons-material';
import { customerService, Customer } from '../../services/customerService';
import { collectionsService, CustomerOpenInvoice } from '../../services/collectionsService';
import { formatIndianNumber } from '../../utils/currency';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill, WorkspaceHeader, TotalsStrip, ValidationStrip,
} from '../../components/common/workspaceKit';

const n = (v: string) => Number(v) || 0;
const MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH', 'BANK_TRANSFER'];

const CustomerPaymentNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<CustomerOpenInvoice[]>([]);
  const [f, setF] = useState({
    customerId: '', invoiceId: '', paymentDate: new Date().toISOString().slice(0, 10), amount: '',
    paymentMode: 'NEFT', bankReference: '', notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { customerService.getCustomers({ is_active: true }).then(setCustomers).catch(() => {}); }, []);
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));

  // When a customer is chosen, load their open (unpaid / partially paid)
  // invoices so the receipt can be applied to one and the amount pre-filled
  // from its outstanding balance.
  const onCustomer = async (customerId: string) => {
    set({ customerId, invoiceId: '', amount: '' });
    setInvoices([]);
    if (!customerId) return;
    try {
      const res = await collectionsService.getCustomerOpenInvoices(Number(customerId));
      setInvoices(res.invoices || []);
    } catch { setInvoices([]); }
  };
  const onInvoice = (invoiceId: string) => {
    const inv = invoices.find((i) => String(i.id) === String(invoiceId));
    if (inv) set({ invoiceId, amount: String(inv.balance ?? inv.totalAmount ?? '') });
    else set({ invoiceId: '', amount: '' });
  };

  const valid = !!f.customerId && !!f.invoiceId && n(f.amount) > 0;

  const submit = async () => {
    setError(null);
    if (!valid) { setError('Customer, an invoice and an amount greater than zero are required'); return; }
    setSaving(true);
    try {
      await collectionsService.recordPayment({
        invoiceId: Number(f.invoiceId),
        amount: n(f.amount),
        date: f.paymentDate,
        method: f.paymentMode,
        reference: f.bankReference || undefined,
        notes: f.notes || undefined,
      });
      setToast('Receipt recorded');
      setTimeout(() => navigate('/finance/customer-payments'), 800);
    } catch (e: any) { setError(e.message || 'Failed to record receipt'); } finally { setSaving(false); }
  };

  const selectedCustomer = customers.find((c) => String(c.id) === String(f.customerId)) || null;
  const selectedInvoice = invoices.find((i) => String(i.id) === String(f.invoiceId)) || null;

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
      <WorkspaceHeader
        onBack={() => navigate('/finance/customer-payments')}
        title="New Customer Receipt"
        subtitle="Record a receipt against a customer invoice and see the bank & AR impact."
        chip={<StatusPill label="DRAFT" tone="draft" />}
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/finance/customer-payments')} disabled={saving} sx={outBtn}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <TaskAlt sx={{ fontSize: 18 }} />}
              onClick={submit}
              disabled={saving || !valid}
              sx={priBtn}
            >
              {saving ? 'Saving…' : 'Record Receipt'}
            </Button>
          </>
        }
      />

      <Box sx={{ px: PX, pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Receipt details */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Receipt Details</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Customer" required />
              <Autocomplete
                size="small"
                options={customers}
                value={selectedCustomer}
                onChange={(_, v) => onCustomer(v ? String(v.id) : '')}
                getOptionLabel={(o) => o.company_name || ''}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                renderInput={(params) => <TextField {...params} placeholder="Select customer" sx={fieldSx} />}
              />
            </Box>
            <Box>
              <FieldLabel text="Against Invoice" required />
              <TextField
                select fullWidth size="small" value={f.invoiceId} onChange={(e) => onInvoice(e.target.value)} sx={fieldSx}
                disabled={!f.customerId}
                helperText={f.customerId && !invoices.length ? 'No open invoices for this customer' : 'Required — the receipt is applied to this invoice'}
              >
                <MenuItem value="" disabled>{invoices.length ? 'Select invoice' : '— none —'}</MenuItem>
                {invoices.map((i) => (
                  <MenuItem key={i.id} value={String(i.id)}>
                    {i.invoiceNumber} · Bal {formatIndianNumber(Number(i.balance ?? i.totalAmount ?? 0))}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Payment Date" required />
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={f.paymentDate} onChange={(e) => set({ paymentDate: e.target.value })} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Payment Mode" />
              <TextField select fullWidth size="small" value={f.paymentMode} onChange={(e) => set({ paymentMode: e.target.value })} sx={fieldSx}>
                {MODES.map((m) => <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Bank Reference" />
              <TextField fullWidth size="small" value={f.bankReference} onChange={(e) => set({ bankReference: e.target.value })} sx={fieldSx} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Notes" />
              <TextField fullWidth size="small" value={f.notes} onChange={(e) => set({ notes: e.target.value })} sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* Amount */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Amount</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Amount Received" required />
              <TextField fullWidth size="small" type="number" value={f.amount} onChange={(e) => set({ amount: e.target.value })} inputProps={{ style: { textAlign: 'right' } }} sx={fieldSx} />
            </Box>
            {selectedInvoice && (
              <Box>
                <FieldLabel text="Invoice Balance" />
                <TextField fullWidth size="small" value={formatIndianNumber(Number(selectedInvoice.balance || 0))} disabled sx={fieldSx} />
              </Box>
            )}
          </Box>
        </Paper>

        {/* Totals + status strip */}
        <Box sx={{ mb: 1.5 }}>
          <TotalsStrip
            cells={[
              { label: 'Customer', value: selectedCustomer?.company_name || '—' },
              { label: 'Invoice', value: selectedInvoice?.invoiceNumber || '—' },
              { label: 'Amount', value: formatIndianNumber(n(f.amount)), tone: 'primary' },
              { label: 'Mode', value: f.paymentMode },
            ]}
          />
        </Box>

        {/* Validation strip */}
        <ValidationStrip
          items={[
            { ok: !!f.customerId, label: 'Customer selected' },
            { ok: !!f.invoiceId, label: 'Invoice selected' },
            { ok: n(f.amount) > 0, label: 'Amount entered' },
          ]}
        />
      </Box>

      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default CustomerPaymentNewPage;
